import { Radio, RadioGroup } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { TimeOfDay, TIME_OPTIONS, CustomTime } from './types'
import { customTimeToTimeValue, timeValueToCustomTime } from './utils'
import { NativeTimePicker } from 'blaze-ui/TimePicker/TimePicker'
import { TimeValue } from '@react-aria/datepicker'
import Styles from './TimeOfDayWithInlinePicker.module.scss'
import cx from 'classnames'

interface TimeOfDayWithInlinePickerProps {
  value: TimeOfDay
  customTime?: CustomTime
  onChange: (timeOfDay: TimeOfDay, customTime?: CustomTime) => void
  className?: string
  ariaLabel?: string
  radioOptionClassName?: string
  selectedClassName?: string
  radioLabelClassName?: string
  showDescriptions?: boolean
  optionTitleClassName?: string
  optionDescriptionClassName?: string
}

export const TimeOfDayWithInlinePicker = ({
  value,
  customTime = { hour: 9, minute: 0, amOrPm: 'am' },
  onChange,
  className,
  ariaLabel = 'Time of day options',
  radioOptionClassName,
  selectedClassName,
  radioLabelClassName,
  showDescriptions = false,
  optionTitleClassName,
  optionDescriptionClassName,
}: TimeOfDayWithInlinePickerProps) => {
  const handleRadioChange = (newValue: TimeOfDay) => {
    if (newValue !== TimeOfDay.Custom) onChange(newValue, undefined)
  }

  const handleTimeChange = (timeValue: TimeValue) => {
    onChange(TimeOfDay.Custom, timeValueToCustomTime(timeValue))
  }

  const presetOptions = [TimeOfDay.Morning, TimeOfDay.Afternoon, TimeOfDay.Evening, TimeOfDay.Night, TimeOfDay.Random]
  const isCustomActive = value === TimeOfDay.Custom

  return (
    <div>
      <div className={cx(Styles.radioSection, { [Styles.dimmed]: isCustomActive })}>
        <RadioGroup
          aria-label={ariaLabel}
          value={value === TimeOfDay.Custom ? '' : value}
          className={className}
          onChange={(newValue) => handleRadioChange(newValue as TimeOfDay)}
        >
          {presetOptions.map((timeOption) => (
            <Radio
              className={radioOptionClassName}
              classNames={{ selected: selectedClassName }}
              value={timeOption}
              key={timeOption}
              hideRadio
            >
              {(radioBulletProps) => (
                <>
                  <div>
                    <RadioBullet {...radioBulletProps} size={20} />
                  </div>
                  {showDescriptions ? (
                    <div className={radioLabelClassName}>
                      <div className={optionTitleClassName}>{TIME_OPTIONS[timeOption].label}</div>
                      {TIME_OPTIONS[timeOption].description && (
                        <div className={optionDescriptionClassName}>{TIME_OPTIONS[timeOption].description}</div>
                      )}
                    </div>
                  ) : (
                    <span className={radioLabelClassName}>{TIME_OPTIONS[timeOption].label}</span>
                  )}
                </>
              )}
            </Radio>
          ))}
        </RadioGroup>
      </div>

      <div
        className={cx(Styles.timePickerSection, { [Styles.active]: isCustomActive })}
        onFocus={() => onChange(TimeOfDay.Custom, customTime)}
        onClick={() => onChange(TimeOfDay.Custom, customTime)}
      >
        <NativeTimePicker
          value={customTimeToTimeValue(customTime)}
          onChange={handleTimeChange}
          showIcon={true}
          showTimezone={true}
          showChangeText={true}
        />
      </div>
    </div>
  )
}
